import { useState, useCallback, useRef } from 'react';
import { authenticatedFetch } from '../../../utils/authenticatedFetch';

/**
 * Hook for handling message pagination with infinite scroll
 * Loads recent messages first, then older messages when scrolling to top
 */
export const useMessagePagination = ({ chatId, setMessages, setIsLoading, setError }) => {
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const isFetchingRef = useRef(false);
  const initialLoadDoneRef = useRef(false);

  // Reset pagination state when chat changes
  const resetPagination = useCallback(() => {
    setHasMoreMessages(false);
    setIsLoadingMore(false);
    setCursor(null);
    isFetchingRef.current = false;
    initialLoadDoneRef.current = false;
  }, []);

  /**
   * Fetch initial messages (most recent 30)
   */
  const fetchInitialMessages = useCallback(async () => {
    if (!chatId || isFetchingRef.current) return;
    
    try {
      isFetchingRef.current = true;
      setIsLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('📄 Fetching initial messages for chat:', chatId);
      
      const response = await authenticatedFetch(
        `${process.env.REACT_APP_API_URL}/chat/${chatId}/messages?limit=30`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch messages: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      const messages = data.messages || [];
      const pagination = data.pagination || {};

      console.log('📄 Fetched initial messages:', {
        count: messages.length,
        hasMore: pagination.hasMore,
        cursor: pagination.cursor
      });

      setMessages(messages);
      setHasMoreMessages(pagination.hasMore || false);
      setCursor(pagination.cursor || null);
      initialLoadDoneRef.current = true;

    } catch (error) {
      console.error('Error fetching initial messages:', error);
      setError(`Failed to load messages: ${error.message}`);
      setMessages([]);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [chatId, setMessages, setIsLoading, setError]);

  /**
   * Load more older messages when scrolling to top
   */
  const loadMoreMessages = useCallback(async () => {
    if (!chatId || !cursor || !hasMoreMessages || isFetchingRef.current || isLoadingMore) {
      return;
    }

    try {
      isFetchingRef.current = true;
      setIsLoadingMore(true);
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('📄 Loading more messages before cursor:', cursor);
      
      const response = await authenticatedFetch(
        `${process.env.REACT_APP_API_URL}/chat/${chatId}/messages?limit=30&before=${cursor}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to load more messages: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      const olderMessages = data.messages || [];
      const pagination = data.pagination || {};

      console.log('📄 Loaded more messages:', {
        count: olderMessages.length,
        hasMore: pagination.hasMore,
        newCursor: pagination.cursor
      });

      if (olderMessages.length > 0) {
        // Prepend older messages to the existing messages
        setMessages(prevMessages => {
          const existingIds = new Set(prevMessages.map(m => m._id));
          const newMessages = olderMessages.filter(m => !existingIds.has(m._id));
          return [...newMessages, ...prevMessages];
        });
        
        setHasMoreMessages(pagination.hasMore || false);
        setCursor(pagination.cursor || null);
      } else {
        setHasMoreMessages(false);
      }

    } catch (error) {
      console.error('Error loading more messages:', error);
      // Don't show error to user for pagination failures, just stop trying
      setHasMoreMessages(false);
    } finally {
      setIsLoadingMore(false);
      isFetchingRef.current = false;
    }
  }, [chatId, cursor, hasMoreMessages, isLoadingMore, setMessages]);

  return {
    fetchInitialMessages,
    loadMoreMessages,
    hasMoreMessages,
    isLoadingMore,
    resetPagination,
    initialLoadDone: initialLoadDoneRef.current
  };
};
